import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Alert,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Fab,
  useTheme,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Edit,
  Delete,
  Download,
  MoreVert,
  Description,
  Image,
  VideoFile,
  AudioFile,
  Archive,
  Email,
  CloudUpload,
  ViewModule,
  ViewList,
  Folder,
  FileCopy,
  PictureAsPdf,
  TextFields,
  TableChart,
  History,
  DescriptionOutlined,
  FolderSpecial,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import DocumentDialog from '../../components/Documents/DocumentDialog';
import DocumentUpload from '../../components/Documents/DocumentUpload';
import DocumentEditor from '../../components/Documents/DocumentEditor';
import DocumentGallery from '../../components/Documents/DocumentGallery';
import DocumentVersionHistory from '../../components/Documents/DocumentVersionHistory';
import DocumentPreviewDialog from '../../components/Documents/DocumentPreviewDialog';
import ImportTemplateToCaseDialog from '../../components/Documents/ImportTemplateToCaseDialog';
import AssignDocumentToCaseDialog from '../../components/Documents/AssignDocumentToCaseDialog';
import ListPagination from '../../components/Common/ListPagination';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import { userHasPermission } from '../../utils/permissions';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';

const hasRecoveredTag = (tags: string[] | string | undefined | null): boolean => {
  if (!tags) return false;
  if (Array.isArray(tags)) return tags.includes('recovered');
  try {
    const parsed = JSON.parse(tags);
    return Array.isArray(parsed) && parsed.includes('recovered');
  } catch {
    return String(tags).includes('recovered');
  }
};

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  documentType: string;
  category: string;
  tags: string[] | string;
  isConfidential: boolean;
  isPublic: boolean;
  isTemplate: boolean;
  version: number;
  caseId: string | null;
  clientId: string | null;
  case?: { id: string; caseNumber?: string; title?: string } | null;
  assignedTo: string[];
  uploadedBy: string;
  createdAt: string | null;
  updatedAt: string | null;
}

interface DocumentsResponse {
  success: boolean;
  data: {
    documents: Document[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

interface Filters {
  search: string;
  documentType: string;
  category: string;
  isConfidential: string;
  caseId: string;
  clientId: string;
  isTemplate: string;
  tags: string;
}

interface DocumentsPageProps {
  /** Lock page to firm templates, matter docs, or show both tabs (legacy). */
  mode?: 'templates' | 'matters' | 'all';
}

const DocumentsPage: React.FC<DocumentsPageProps> = ({ mode = 'all' }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const canDeleteDocuments = userHasPermission(user, 'documents:delete');
  const [filters, setFilters] = useState<Filters>({
    search: '',
    documentType: '',
    category: '',
    isConfidential: '',
    caseId: '',
    clientId: '',
    isTemplate: '',
    tags: '',
  });
  const [hideRecovered, setHideRecovered] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'create'>('create');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editorDialogOpen, setEditorDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionHistoryDoc, setVersionHistoryDoc] = useState<Document | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDocumentForMenu, setSelectedDocumentForMenu] = useState<Document | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'gallery'>('list');
  // When mode is locked (route /templates vs /documents), derive the tab from `mode`.
  // React Router reuses this component instance across those routes, so useState(mode)
  // only ran on first mount and left the wrong library visible after navigation.
  const [manualTab, setManualTab] = useState<'templates' | 'case-specific'>('templates');
  const activeTab: 'templates' | 'case-specific' =
    mode === 'matters' ? 'case-specific' : mode === 'templates' ? 'templates' : manualTab;
  const showTabs = mode === 'all';
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importTemplateDoc, setImportTemplateDoc] = useState<Document | null>(null);
  const [assignCaseDoc, setAssignCaseDoc] = useState<Document | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const queryClient = useQueryClient();

  // Clear template-only filters when entering Matter Documents (and vice versa)
  useEffect(() => {
    if (mode === 'matters') {
      setFilters((prev) => (prev.tags || prev.isTemplate ? { ...prev, tags: '', isTemplate: '' } : prev));
      setHideRecovered(true);
    }
  }, [mode]);

  // Any filter/tab change invalidates the current page position.
  useEffect(() => {
    setPage(1);
  }, [filters, activeTab, hideRecovered]);

  // Fetch documents
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['documents', user?.tenantId, filters, activeTab, hideRecovered, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (!value) return;
        if (key === 'search') {
          params.append('q', value);
        } else {
          params.append(key, value);
        }
      });

      if (activeTab === 'templates') {
        params.append('isTemplate', 'true');
        // Quarantine view: only recovered; otherwise server returns all and we may hide client-side
        if (filters.tags === 'recovered') {
          params.set('tags', 'recovered');
        }
      } else {
        params.append('isTemplate', 'false');
      }

      // The library endpoint is offset-based and defaults to 50; without these
      // the page silently showed only the newest 50 documents with no pager.
      params.append('limit', String(limit));
      params.append('offset', String((page - 1) * limit));

      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/documents?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to load documents.'));
      }

      return response.json();
    },
    retry: 1,
    retryDelay: 1000,
  });

  // Delete document mutation
  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to delete document.'));
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents-count'] });
      toast.success('Document deleted successfully');
    },
    onError: (error) => {
      console.error('Delete mutation error:', error);
      toast.error(toUserErrorMessage(error, 'Unable to delete document.'));
    },
  });

  const { data: templateCountData } = useQuery({
    queryKey: ['documents-count', user?.tenantId, 'templates'],
    queryFn: async () => {
      const token = getAuthToken();
      const response = await fetch('/api/documents?isTemplate=true&limit=1', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return { total: 0 };
      const json = await response.json();
      return { total: json.data?.pagination?.total ?? 0 };
    },
  });

  const { data: matterCountData } = useQuery({
    queryKey: ['documents-count', user?.tenantId, 'matter'],
    queryFn: async () => {
      const token = getAuthToken();
      const response = await fetch('/api/documents?isTemplate=false&limit=1', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return { total: 0 };
      const json = await response.json();
      return { total: json.data?.pagination?.total ?? 0 };
    },
  });

  const copyDocumentMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const response = await fetch(`/api/documents/${documentId}/copy`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to copy document.'));
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents-count'] });
      toast.success('Library copy created');
    },
    onError: (error: Error) => {
      toast.error(toUserErrorMessage(error, 'Unable to copy document.'));
    },
  });

  const assignCaseMutation = useMutation({
    mutationFn: async ({ documentId, caseId }: { documentId: string; caseId: string | null }) => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');
      const response = await fetch(`/api/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ caseId: caseId || null }),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to assign this document to a matter.'));
      }
      return response.json();
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents-count'] });
      queryClient.invalidateQueries({ queryKey: ['case-documents'] });
      queryClient.invalidateQueries({ queryKey: ['matter-drive'] });
      queryClient.invalidateQueries({ queryKey: ['matter-drive-tree'] });
      toast.success(variables.caseId ? 'Document filed to the matter' : 'Document removed from the matter');
      setAssignCaseDoc(null);
    },
    onError: (error: Error) => {
      toast.error(toUserErrorMessage(error, 'Unable to assign this document to a matter.'));
    },
  });

  const saveDocumentMutation = useMutation({
    mutationFn: async (documentData: Partial<Document> & { id: string; clearRecovered?: boolean }) => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required');

      let tags = documentData.tags;
      if (documentData.clearRecovered) {
        const list = Array.isArray(tags)
          ? tags
          : (typeof tags === 'string'
            ? (() => { try { const p = JSON.parse(tags); return Array.isArray(p) ? p : []; } catch { return []; } })()
            : []);
        tags = list.filter((t: string) => t !== 'recovered');
      }

      const response = await fetch(`/api/documents/${documentData.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: documentData.title,
          description: documentData.description,
          documentType: documentData.documentType,
          category: documentData.category,
          tags,
          isConfidential: documentData.isConfidential,
          isPublic: documentData.isPublic,
          isTemplate: documentData.isTemplate,
        }),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to save document.'));
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Document saved');
      setDialogOpen(false);
    },
    onError: (error: Error) => {
      toast.error(toUserErrorMessage(error, 'Unable to save document.'));
    },
  });

  // Download document
  const downloadDocument = async (documentId: string, fileName: string) => {
    try {
      const response = await fetch(`/api/documents/${documentId}/download`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to download document'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      toast.error(error.message || 'Failed to download document');
    }
  };

  const handleCreateDocument = () => {
    setSelectedDocument(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  // View = open the actual file (practice UX). Properties live under Edit.
  const handleViewDocument = (document: Document) => {
    setPreviewDocument(document);
    setPreviewOpen(true);
  };

  const handleEditDocument = (document: Document) => {
    setSelectedDocument(document);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleOpenEditor = (document: Document) => {
    setSelectedDocument(document);
    setEditorDialogOpen(true);
  };

  const handleOpenGallery = () => {
    setGalleryDialogOpen(true);
  };

  const handleImportToCase = (document: Document) => {
    setImportTemplateDoc(document);
    setImportDialogOpen(true);
  };

  const handleRetitleRecovered = (document: Document) => {
    setSelectedDocument(document);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleOpenVersionHistory = (document: Document) => {
    setVersionHistoryDoc(document);
    setVersionHistoryOpen(true);
  };

  const handleDelete = (documentId: string) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteMutation.mutate(documentId);
    }
  };

  const handleSaveDocument = async (documentData: Partial<Document>) => {
    const id = documentData.id || selectedDocument?.id;
    if (dialogMode === 'create' || !id) {
      toast.error('Use Upload Template to create new firm templates');
      return;
    }
    const wasRecovered = hasRecoveredTag(selectedDocument?.tags) || hasRecoveredTag(documentData.tags);
    const titleChanged = documentData.title && documentData.title !== selectedDocument?.title;
    saveDocumentMutation.mutate({
      ...documentData,
      id,
      clearRecovered: wasRecovered && Boolean(titleChanged),
    } as Partial<Document> & { id: string; clearRecovered?: boolean });
  };

  const documentsList: Document[] = (data?.data?.documents || []).filter((doc: Document) => {
    if (activeTab !== 'templates') return true;
    if (filters.tags === 'recovered') return true;
    if (hideRecovered && hasRecoveredTag(doc.tags)) return false;
    return true;
  });
  const documentsTotal: number = data?.data?.pagination?.total ?? documentsList.length;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, document: Document) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocumentForMenu(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocumentForMenu(null);
  };

  const getDocumentTypeIcon = (documentType: string) => {
    switch (documentType) {
      case 'image':
        return <Image />;
      case 'video':
        return <VideoFile />;
      case 'audio':
        return <AudioFile />;
      case 'archive':
        return <Archive />;
      case 'email_attachment':
        return <Email />;
      case 'pdf':
        return <PictureAsPdf />;
      case 'text':
        return <TextFields />;
      case 'spreadsheet':
        return <TableChart />;
      default:
        return <Description />;
    }
  };

  const getDocumentTypeColor = (documentType: string) => {
    switch (documentType) {
      case 'pleading':
        return 'primary';
      case 'evidence':
        return 'error';
      case 'correspondence':
        return 'info';
      case 'contract':
        return 'success';
      case 'invoice':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return 'N/A';
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return 'N/A';
    }
  };

  if (error) {
    console.error('Documents error:', error);
  }

  return (
    <Box>
      {error && (
        <ListLoadErrorAlert
          resourceLabel={activeTab === 'templates' ? 'firm templates' : 'matter documents'}
          onRetry={() => refetch()}
        />
      )}
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {activeTab === 'templates' ? 'Firm Templates' : 'Matter Documents'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ViewModule />}
              onClick={handleOpenGallery}
            >
              Gallery View
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setUploadDialogOpen(true)}
            >
              {activeTab === 'templates' ? 'Upload Template' : 'Upload Document'}
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {activeTab === 'templates'
            ? 'Firm form library. Import a template into a case from here or from Case Folder.'
            : 'Documents attached to matters. Use Firm Templates to import reusable forms.'}
        </Typography>
      </Box>

      {/* Category Tabs (only when mode=all) */}
      {showTabs && (
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setManualTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value="templates"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionOutlined fontSize="small" />
                Firm Templates
                <Badge badgeContent={templateCountData?.total || 0} color="primary" />
              </Box>
            }
          />
          <Tab
            value="case-specific"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Folder fontSize="small" />
                Matter Documents
                <Badge badgeContent={matterCountData?.total || 0} color="primary" />
              </Box>
            }
          />
        </Tabs>
      </Paper>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search documents..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={filters.documentType}
                  onChange={(e) => setFilters({ ...filters, documentType: e.target.value })}
                  label="Document Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="pleading">Pleading</MenuItem>
                  <MenuItem value="discovery">Discovery</MenuItem>
                  <MenuItem value="correspondence">Correspondence</MenuItem>
                  <MenuItem value="evidence">Evidence</MenuItem>
                  <MenuItem value="court_filing">Court Filing</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="agreement">Agreement</MenuItem>
                  <MenuItem value="invoice">Invoice</MenuItem>
                  <MenuItem value="receipt">Receipt</MenuItem>
                  <MenuItem value="id_document">ID Document</MenuItem>
                  <MenuItem value="medical_record">Medical Record</MenuItem>
                  <MenuItem value="police_report">Police Report</MenuItem>
                  <MenuItem value="expert_report">Expert Report</MenuItem>
                  <MenuItem value="witness_statement">Witness Statement</MenuItem>
                  <MenuItem value="photograph">Photograph</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="audio">Audio</MenuItem>
                  <MenuItem value="email_attachment">Email Attachment</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="pleadings">Pleadings</MenuItem>
                  <MenuItem value="discovery">Discovery</MenuItem>
                  <MenuItem value="correspondence">Correspondence</MenuItem>
                  <MenuItem value="contracts">Contracts</MenuItem>
                  <MenuItem value="intake">Intake</MenuItem>
                  <MenuItem value="motions">Motions</MenuItem>
                  <MenuItem value="forms">Forms</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Confidential</InputLabel>
                <Select
                  value={filters.isConfidential}
                  onChange={(e) => setFilters({ ...filters, isConfidential: e.target.value })}
                  label="Confidential"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Confidential</MenuItem>
                  <MenuItem value="false">Public</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {activeTab === 'templates' && (
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Recovered</InputLabel>
                  <Select
                    value={filters.tags === 'recovered' ? 'quarantine' : hideRecovered ? 'hide' : 'show'}
                    label="Recovered"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'quarantine') {
                        setHideRecovered(false);
                        setFilters({ ...filters, tags: 'recovered' });
                      } else if (v === 'hide') {
                        setHideRecovered(true);
                        setFilters({ ...filters, tags: '' });
                      } else {
                        setHideRecovered(false);
                        setFilters({ ...filters, tags: '' });
                      }
                    }}
                  >
                    <MenuItem value="hide">Hide recovered</MenuItem>
                    <MenuItem value="show">Show all</MenuItem>
                    <MenuItem value="quarantine">Quarantine only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setHideRecovered(true);
                  setFilters({
                    search: '',
                    documentType: '',
                    category: '',
                    isConfidential: '',
                    caseId: '',
                    clientId: '',
                    isTemplate: '',
                    tags: '',
                  });
                }}
              >
                Clear Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={activeTab === 'templates' ? 2 : 3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={viewMode === 'list' ? 'contained' : 'outlined'}
                  startIcon={<ViewList />}
                  onClick={() => setViewMode('list')}
                >
                  List
                </Button>
                <Button
                  variant={viewMode === 'gallery' ? 'contained' : 'outlined'}
                  startIcon={<ViewModule />}
                  onClick={() => setViewMode('gallery')}
                >
                  Gallery
                </Button>
              </Box>
            </Grid>
          </Grid>
          {activeTab === 'templates' && filters.tags === 'recovered' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Quarantine view: recovered orphan templates need retitling before demo use. Edit a title to clear the recovered tag.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Documents Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : documentsList.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Folder sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {activeTab === 'templates'
                ? 'No firm templates yet'
                : 'No matter documents exist'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
              {activeTab === 'templates'
                ? 'Upload a template to the firm library, or import recovered files after curation.'
                : 'Upload a document or import a firm template into a case to get started.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setUploadDialogOpen(true)}
            >
              {activeTab === 'templates' ? 'Upload Template' : 'Upload Document'}
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        /* List View */
        <Card>
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Document</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentsList.map((document) => (
                    <TableRow key={document.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {getDocumentTypeIcon(document.documentType)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {document.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {document.originalName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={document.documentType}
                          color={getDocumentTypeColor(document.documentType) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {document.category || 'Uncategorized'}
                      </TableCell>
                      <TableCell>
                        {formatFileSize(document.fileSize)}
                      </TableCell>
                      <TableCell>
                        {formatDate(document.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {document.isTemplate && (
                            <Chip label="Template" color="info" size="small" />
                          )}
                          {hasRecoveredTag(document.tags) && (
                            <Chip
                              label="Needs retitle"
                              color="warning"
                              size="small"
                              onClick={() => handleRetitleRecovered(document)}
                            />
                          )}
                          {document.isConfidential && (
                            <Chip label="Confidential" color="error" size="small" />
                          )}
                          {document.isPublic && (
                            <Chip label="Public" color="success" size="small" />
                          )}
                          {!document.isTemplate && (
                            document.case?.id || document.caseId ? (
                              <Chip
                                label={document.case?.caseNumber || 'Filed to a case'}
                                color="primary"
                                variant="outlined"
                                size="small"
                                onClick={() => setAssignCaseDoc(document)}
                              />
                            ) : (
                              <Chip
                                label="Unfiled"
                                color="default"
                                variant="outlined"
                                size="small"
                                onClick={() => setAssignCaseDoc(document)}
                              />
                            )
                          )}
                          {!document.isTemplate && document.assignedTo && document.assignedTo.length > 0 && (
                            <Chip label={`${document.assignedTo.length} assigned`} color="warning" size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDocument(document)}
                        >
                          <Visibility />
                        </IconButton>
                        {!document.isTemplate && (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditor(document)}
                          >
                            <Edit />
                          </IconButton>
                        )}
                        {document.isTemplate && (
                          <IconButton
                            size="small"
                            onClick={() => handleImportToCase(document)}
                          >
                            <FileCopy />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => downloadDocument(document.id, document.originalName)}
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, document)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <ListPagination
              page={page}
              limit={limit}
              total={documentsTotal}
              onPageChange={setPage}
              onLimitChange={(next) => {
                setLimit(next);
                setPage(1);
              }}
            />
          </CardContent>
        </Card>
      ) : (
        /* Gallery View */
        <Grid container spacing={3}>
          {documentsList.map((document) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={document.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: theme.shadows[8],
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {getDocumentTypeIcon(document.documentType)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {document.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {document.originalName}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                    {document.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={document.documentType}
                      color={getDocumentTypeColor(document.documentType) as any}
                      size="small"
                    />
                    {document.isTemplate && (
                      <Chip label="Template" color="info" size="small" />
                    )}
                    {hasRecoveredTag(document.tags) && (
                      <Chip
                        label="Needs retitle"
                        color="warning"
                        size="small"
                        onClick={() => handleRetitleRecovered(document)}
                      />
                    )}
                    {document.isConfidential && (
                      <Chip label="Confidential" color="error" size="small" />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(document.fileSize)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(document.createdAt)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDocument(document)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    {!document.isTemplate && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditor(document)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    )}
                    {document.isTemplate && (
                      <Tooltip title="Import to Case">
                        <IconButton
                          size="small"
                          onClick={() => handleImportToCase(document)}
                        >
                          <FileCopy />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={() => downloadDocument(document.id, document.originalName)}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, document)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12}>
            <ListPagination
              page={page}
              limit={limit}
              total={documentsTotal}
              onPageChange={setPage}
              onLimitChange={(next) => {
                setLimit(next);
                setPage(1);
              }}
            />
          </Grid>
        </Grid>
      )}

      <DocumentPreviewDialog
        open={previewOpen}
        document={previewDocument}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewDocument(null);
        }}
        onEditContent={(doc) => {
          setPreviewOpen(false);
          handleOpenEditor(doc as Document);
        }}
        onEditProperties={(doc) => {
          setPreviewOpen(false);
          handleEditDocument(doc as Document);
        }}
        onDownload={(doc) => {
          downloadDocument(doc.id, doc.originalName || doc.fileName || 'document');
        }}
      />

      {/* Document properties (Edit) — not used for View */}
      <DocumentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        documentData={selectedDocument}
        mode={dialogMode === 'view' ? 'edit' : dialogMode}
        onSave={handleSaveDocument}
      />

      {/* Document Upload Dialog */}
      <DocumentUpload
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        defaultIsTemplate={activeTab === 'templates'}
        onSuccess={() => {
          setUploadDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: ['documents'] });
          queryClient.invalidateQueries({ queryKey: ['documents-count'] });
        }}
      />

      <ImportTemplateToCaseDialog
        open={importDialogOpen}
        onClose={() => {
          setImportDialogOpen(false);
          setImportTemplateDoc(null);
        }}
        template={importTemplateDoc}
      />

      <AssignDocumentToCaseDialog
        open={Boolean(assignCaseDoc)}
        onClose={() => setAssignCaseDoc(null)}
        documentTitle={assignCaseDoc?.title}
        currentCaseId={assignCaseDoc?.caseId ?? null}
        currentCaseNumber={assignCaseDoc?.case?.caseNumber ?? null}
        saving={assignCaseMutation.isPending}
        onAssign={(caseId) => {
          if (assignCaseDoc) {
            assignCaseMutation.mutate({ documentId: assignCaseDoc.id, caseId });
          }
        }}
      />

      {versionHistoryDoc && (
        <DocumentVersionHistory
          open={versionHistoryOpen}
          onClose={() => {
            setVersionHistoryOpen(false);
            setVersionHistoryDoc(null);
          }}
          documentId={versionHistoryDoc.id}
          documentTitle={versionHistoryDoc.title}
          onVersionRestored={() => {
            queryClient.invalidateQueries({ queryKey: ['documents'] });
          }}
        />
      )}

      {/* Document Editor Dialog */}
      <DocumentEditor
        open={editorDialogOpen}
        onClose={() => setEditorDialogOpen(false)}
        document={selectedDocument}
        onSave={() => {
          setEditorDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: ['documents'] });
        }}
      />

      {/* Document Gallery Dialog */}
      <DocumentGallery
        open={galleryDialogOpen}
        onClose={() => setGalleryDialogOpen(false)}
        documents={documentsList}
        onDocumentClick={handleViewDocument}
        onDocumentEdit={handleEditDocument}
        onDocumentDownload={downloadDocument}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedDocumentForMenu) {
            handleViewDocument(selectedDocumentForMenu);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View document</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedDocumentForMenu) {
            handleOpenEditor(selectedDocumentForMenu);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit document</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedDocumentForMenu) {
            handleEditDocument(selectedDocumentForMenu);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {hasRecoveredTag(selectedDocumentForMenu?.tags) ? 'Retitle / Edit properties' : 'Edit properties'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedDocumentForMenu) {
            handleOpenVersionHistory(selectedDocumentForMenu);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <History fontSize="small" />
          </ListItemIcon>
          <ListItemText>Version History</ListItemText>
        </MenuItem>
        {selectedDocumentForMenu?.isTemplate && (
          <MenuItem onClick={() => {
            if (selectedDocumentForMenu) {
              handleImportToCase(selectedDocumentForMenu);
            }
            handleMenuClose();
          }}>
            <ListItemIcon>
              <FileCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText>Import to Case</ListItemText>
          </MenuItem>
        )}
        {!selectedDocumentForMenu?.isTemplate && (
          <MenuItem onClick={() => {
            setAssignCaseDoc(selectedDocumentForMenu);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <FolderSpecial fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {selectedDocumentForMenu?.caseId ? 'Change / remove case assignment' : 'Assign to case'}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          if (selectedDocumentForMenu) {
            copyDocumentMutation.mutate(selectedDocumentForMenu.id);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <FileCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate in Library</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedDocumentForMenu) {
            downloadDocument(selectedDocumentForMenu.id, selectedDocumentForMenu.originalName);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        {canDeleteDocuments && (
          <MenuItem onClick={() => {
            if (selectedDocumentForMenu) {
              handleDelete(selectedDocumentForMenu.id);
            }
            handleMenuClose();
          }}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Floating Action Button */}
      {/* Offset above global TimerWidget FAB (bottom-right) */}
      <Fab
        color="primary"
        aria-label="upload"
        sx={{ position: 'fixed', bottom: 88, right: 16, zIndex: 1200 }}
        onClick={() => setUploadDialogOpen(true)}
      >
        <CloudUpload />
      </Fab>
    </Box>
  );
};

export default DocumentsPage; 